import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, MapPin } from 'lucide-react';
import type { Checkin } from '@awwyeah/shared';
import { checkinPostPath } from '@awwyeah/shared';
import { CheckinPhoto } from './components/CheckinPhoto';
import { archiveSummary, filterByAuthor, groupByMonth } from './feed';
import { fetchCheckinStorage } from './storageApi';
import type { MemberDirectory } from './members';
import { useMembers } from './members';
import { useCheckins } from './useCheckins';
import { memberTint, textOnMemberColor } from '../../theme/memberColor';

/**
 * The archive (design: "Archive — it all keeps").
 *
 * Month grids, a member dot bottom-left, a pin mark when a check-in has a place, and
 * text-only check-ins as quote tiles so the grid doesn't have holes. Per-person browsing
 * lives here and only here — the feed is everyone, always.
 *
 * The one number on the screen is the storage line, and it is about the archive rather
 * than about any person: nothing expires, so it's fair to say what that costs.
 */
export function CheckinArchivePage(): JSX.Element {
  const { checkins } = useCheckins();
  const members = useMembers();
  const [author, setAuthor] = useState<string | null>(null);
  const [bytes, setBytes] = useState(0);

  useEffect(() => {
    let live = true;
    void fetchCheckinStorage().then((storage) => {
      if (live && storage) setBytes(storage.bytes);
    });
    return () => {
      live = false;
    };
  }, []);

  const filtered = useMemo(() => filterByAuthor(checkins, author), [checkins, author]);
  const months = useMemo(() => groupByMonth(filtered), [filtered]);
  const roster = members.all();

  return (
    <div className="-mx-4 flex min-h-[70vh] flex-col" data-testid="checkin-archive">
      <header className="flex h-14 items-center gap-2 border-b border-line bg-surface pl-3 pr-2">
        <Link
          to="/checkin"
          className="flex h-11 w-11 items-center justify-center text-fg-2"
          aria-label="Back to the feed"
        >
          <ChevronLeft size={20} strokeWidth={1.75} />
        </Link>
        <span className="flex-1 font-display text-lg tracking-tight text-fg">
          {author === null ? 'Everything' : members.get(author).name}
        </span>
      </header>

      <div className="flex h-14 items-center gap-2 overflow-x-auto border-b border-line bg-surface px-4">
        <button
          type="button"
          onClick={() => setAuthor(null)}
          className={`flex h-10 flex-shrink-0 items-center rounded-full px-3.5 text-sm font-semibold ${
            author === null ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
          }`}
        >
          Everyone
        </button>
        {roster.map((member) => {
          const active = author === member.id;
          return (
            <button
              key={member.id}
              type="button"
              onClick={() => setAuthor(active ? null : member.id)}
              aria-label={member.name}
              aria-pressed={active}
              style={
                active
                  ? { borderColor: member.color, backgroundColor: memberTint(member.color) }
                  : undefined
              }
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full p-1 ${
                active ? 'border-2' : 'border border-line'
              }`}
            >
              <span
                style={{ backgroundColor: member.color, color: textOnMemberColor(member.color) }}
                className="flex h-full w-full items-center justify-center rounded-full text-[13px] font-bold"
              >
                {member.avatarEmoji ?? member.initial}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-4 pb-28">
        {months.map((month) => (
          <section key={month.key} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
                {month.label}
              </span>
              <span className="text-xs text-fg-3">
                {month.items.length} check-in{month.items.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 lg:grid-cols-6">
              {month.items.map((checkin) => (
                <ArchiveTile key={checkin.id} checkin={checkin} members={members} />
              ))}
            </div>
          </section>
        ))}

        {months.length === 0 && (
          <div className="flex flex-col gap-2 py-10">
            <div className="w-12 border-t-2 border-fg" />
            <div className="text-[19px] font-bold tracking-tight text-fg">Nothing here yet</div>
            <div className="text-[15px] text-fg-2">
              {author === null
                ? 'The archive fills itself in as you go.'
                : `${members.get(author).name} hasn’t checked in yet.`}
            </div>
          </div>
        )}

        <div className="mt-auto flex flex-col gap-1.5 border-t border-line pt-3">
          <span className="text-[13px] text-fg-2" data-testid="checkin-storage">
            <strong className="font-semibold">Nothing expires.</strong>{' '}
            {archiveSummary(checkins, bytes)}
          </span>
          <span className="text-xs text-fg-3">
            Later: a “this week last year” surface, and a map of everywhere the four of you have
            checked in from.
          </span>
        </div>
      </div>
    </div>
  );
}

function ArchiveTile({
  checkin,
  members,
}: {
  checkin: Checkin;
  members: MemberDirectory;
}): JSX.Element {
  const author = members.get(checkin.authorId);

  // Text-only check-ins become quote tiles, so the grid never has holes.
  if (!checkin.photoRef) {
    return (
      <Link
        to={checkinPostPath(checkin.id)}
        className="flex aspect-square flex-col justify-between border border-line bg-surface p-1.5"
        data-testid="checkin-archive-tile"
      >
        <span className="line-clamp-4 text-[11px] leading-[1.3] text-fg-2">“{checkin.text}”</span>
        <span style={{ backgroundColor: author.color }} className="h-3.5 w-3.5 rounded-full" />
      </Link>
    );
  }

  return (
    <Link
      to={checkinPostPath(checkin.id)}
      className="relative aspect-square border border-line bg-raised"
      data-testid="checkin-archive-tile"
    >
      <CheckinPhoto
        photoRef={checkin.photoRef}
        alt={checkin.text ?? `${author.name}’s check-in`}
        className="!h-full !border-0 object-cover"
        variant="thumb"
      />
      <span
        style={{ backgroundColor: author.color }}
        className="absolute bottom-1 left-1 h-3.5 w-3.5 rounded-full border-2 border-surface"
      />
      {checkin.place && (
        <span className="absolute bottom-1 right-1 text-fg-2">
          <MapPin size={12} strokeWidth={2.5} />
        </span>
      )}
    </Link>
  );
}
